import React, {
  useEffect,
  useRef,
  useState,
} from "react";

import ParametersTable from "./ParametersTable";
import SpecificationsAccordion from "./SpecificationsAccordion";

import {
  ParameterAPI,
  MeasureAPI,
  BlobAPI,
} from "../services/api";

import { useSession } from "../context/SessionContext";

type Props = {
  projectId: number;
  selectedNode: any;
};

const LENS_SIZE = 240;
const ZOOM = 2.5;

export default function NotationDetailsPanel({
  projectId,
  selectedNode,
}: Props) {

  const { sessionNo } = useSession();

  const [parameters, setParameters] =
    useState<any[]>([]);

  const [loadingParams, setLoadingParams] =
    useState(false);

  const [imageUrl, setImageUrl] =
    useState<string | null>(null);

  const [showLens, setShowLens] =
    useState(false);

  const [lensPos, setLensPos] =
    useState({
      x: 0,
      y: 0,
    });

  const [mousePos, setMousePos] =
    useState({
      x: 0,
      y: 0,
    });

  const imageRef =
    useRef<HTMLImageElement>(null);

  const lensCanvasRef =
    useRef<HTMLCanvasElement>(null);

      useEffect(() => {

    if (
      !selectedNode ||
      sessionNo == null
    ) {

      setParameters([]);

      return;

    }

    const load = async () => {

      try {

        setLoadingParams(true);

        const data =
          await ParameterAPI.getBySet(

            selectedNode.tag,

            projectId,

            sessionNo

          );

        setParameters(data);

      }

      catch (err) {

        console.error(err);

        setParameters([]);

      }

      finally {

        setLoadingParams(false);

      }

    };

    load();

  }, [

    selectedNode,

    projectId,

    sessionNo

  ]);
    const handleUpdateParameters =
    async (

      updated: any[],

      index: number,

      field: string

    ) => {

      const item = updated[index];

      try {

        if (field === "value") {

          const result =
            await MeasureAPI.update({

              wmno: item.id,

              sno: sessionNo!,

              mval: Number(item.value),

              axn: "ParameterUpdate",

            });

          const changed =
            result.Q_Parameters ?? [];

          if (changed.length > 0) {

            setParameters(

              changed.map((p: any) => ({

                id: p.ParameterNo,

                parameter: p.Parameter,

                value: p.Value,

              }))

            );

          }

          else {

            setParameters(updated);

          }

        }

      }

      catch (err) {

        console.error(err);

      }

    };
      useEffect(() => {

    const loadImage = async () => {

      if (!selectedNode) {

        setImageUrl(null);

        return;

      }

      try {

        let url = null;

        if (

          selectedNode.notationNo &&

          selectedNode.notationHead

        ) {

          url =
            await BlobAPI.getImage(

              "Notation1",

              selectedNode.notationHead

            );

        }

        else if (

          selectedNode.wbsNo &&

          selectedNode.sequence

        ) {

          url =
            await BlobAPI.getImage(

              "WorkTree1",

              selectedNode.sequence

            );

        }

        setImageUrl(url);

      }

      catch {

        setImageUrl(null);

      }

    };

    loadImage();

  }, [

    selectedNode

  ]);

  useEffect(() => {

  if (
    !showLens ||
    !imageRef.current ||
    !lensCanvasRef.current
  ) {
    return;
  }

  const img = imageRef.current;

  const canvas = lensCanvasRef.current;

  const ctx = canvas.getContext("2d");

  if (!ctx) return;

  const scaleX =
    img.naturalWidth / img.clientWidth;

  const scaleY =
    img.naturalHeight / img.clientHeight;

  const sourceSize = 200;

  const sx =
    mousePos.x * scaleX -
    sourceSize / 2;

  const sy =
    mousePos.y * scaleY -
    sourceSize / 2;

  ctx.clearRect(
    0,
    0,
    LENS_SIZE,
    LENS_SIZE
  );

  ctx.imageSmoothingEnabled = true;

  ctx.imageSmoothingQuality = "high";

  ctx.drawImage(

    img,

    sx,

    sy,

    sourceSize,

    sourceSize,

    0,

    0,

    LENS_SIZE,

    LENS_SIZE

  );

}, [

  mousePos,

  showLens

]);
const handleImageMove = (

  e: React.MouseEvent<HTMLDivElement>

) => {

  if (!imageRef.current) return;

  const imageRect =
    imageRef.current.getBoundingClientRect();

  const containerRect =
    e.currentTarget.getBoundingClientRect();

  const displayX =
    e.clientX - imageRect.left;

  const displayY =
    e.clientY - imageRect.top;

  if (

    displayX < 0 ||

    displayY < 0 ||

    displayX > imageRect.width ||

    displayY > imageRect.height

  ) {

    setShowLens(false);

    return;

  }

  setShowLens(true);

  setMousePos({

    x: displayX,

    y: displayY,

  });

  setLensPos({

    x:

      imageRect.left -

      containerRect.left +

      displayX,

    y:

      imageRect.top -

      containerRect.top +

      displayY,

  });

};
const hideLens = () => {

  setShowLens(false);

};
return (

<div className="h-full w-full">

  <div className="h-full">

    <div className="grid grid-cols-2 h-full">

      {/* ===========================================
          MIDDLE PANEL
      =========================================== */}

      <div className="flex flex-col h-full bg-white border-r border-gray-300">

        <div className="px-4 pt-4 pb-2">

          <h2
            className="text-gray-700 flex items-center gap-2"
            style={{
              fontFamily: "Segoe UI",
              fontSize: "13px",
              fontWeight: 600,
            }}
          >
            📊 Detail Area
          </h2>

        </div>

        <div
          className="flex-1 overflow-y-auto"
          style={{
            background: "#fff",
          }}
        >

          {
            parameters.length === 0 ?

            (

              <div
                className="flex items-center justify-center h-40 text-gray-400"
              >

                Select a notation

              </div>

            )

            :

            (

              <ParametersTable

                data={parameters}

                loading={loadingParams}

                onUpdate={handleUpdateParameters}

              />

            )

          }

          <SpecificationsAccordion

            projectId={projectId}

            selectedNode={selectedNode}

          />

        </div>

      </div>

      {/* ===========================================
          RIGHT PANEL
      =========================================== */}

      <div className="flex flex-col h-full bg-white">

        <div className="flex-1 overflow-hidden">

          <div
            className="w-full h-full bg-white relative"
          >

            {

              imageUrl ?

              (

                <div

                  className="relative w-full h-full flex items-center justify-center overflow-hidden"

                  onMouseEnter={() => setShowLens(true)}

                  onMouseLeave={() => setShowLens(false)}

                  onMouseMove={handleImageMove}

                >

                  <img

                    ref={imageRef}

                    src={imageUrl}

                    alt="Preview"

                    draggable={false}

                    style={{

                      width: "100%",

                      height: "100%",

                      maxWidth: "100%",

                      maxHeight: "100%",

                      objectFit: "contain",

                      objectPosition: "center",

                      userSelect: "none",

                      pointerEvents: "none",

                      display: "block",

                    }}

                  />

                  {

                    showLens &&

                    imageRef.current &&

                    (

                      <div

                        style={{

                          position: "absolute",

                          left:

                            lensPos.x -

                            LENS_SIZE / 2,

                          top:

                            lensPos.y -

                            LENS_SIZE / 2,

                          width: LENS_SIZE,

                          height: LENS_SIZE,

                          borderRadius: "50%",

                          overflow: "hidden",

                          border: "4px solid black",

                          background: "#fff",

                          pointerEvents: "none",

                          boxShadow:

                            "0 0 15px rgba(0,0,0,.45)",

                          zIndex: 100,

                        }}

                      >

                        <canvas

                          ref={lensCanvasRef}

                          width={LENS_SIZE}

                          height={LENS_SIZE}

                          style={{

                            width: "100%",

                            height: "100%",

                            display: "block",

                          }}

                        />

                      </div>

                    )

                  }

                </div>

              )

              :

              (

                <div
                  className="flex items-center justify-center h-full text-gray-400"
                >

                  No Image

                </div>

              )

            }

          </div>

        </div>

      </div>

    </div>
 
  </div>

</div>

);


}